import { PedigreePerson } from './Person.js';
import {
  PedigreeSiblingGroup,
  PedigreeSiblingSubGroup
} from './SiblingGroup.js';
import { config, SIBLING_TYPE, BASE_LEVEL_HEIGHT } from './config.js';
import { buildRowDepthGroups, normalizeXToStartAtZero } from './helpers.js';

export class PedigreeFamily {
  constructor(familyId) {
    this.id = familyId;
    this.persons = [];
    this.siblingGroups = [];
    this.minRowDepth = 0;
    this.maxRowDepth = 0;
    this.componentSortOrder = new Map();
    // pid → person index. getPersonById was a LINEAR scan, and it is the engine's hottest
    // primitive: every Person relationship getter (spouses/children/father/mother) maps ids
    // through it, so buildRenderModel's union/edge pass alone was O(N²·degree) — measured at
    // 428 people as ~10ms per scoring rebuild, 8.7s of a 10.7s crossing-refine (83%), and the
    // seed pipeline pays the same tax. addPerson is the ONLY insertion point and pids are
    // never reassigned after construction (verified engine-wide), so a Map kept in sync there
    // is exact. First-insertion-wins mirrors find()'s first-match semantics for a duplicate
    // pid.
    this._personByPid = new Map();
  }

  getPersonById(pid) {
    const p = this._personByPid.get(pid);
    return p !== undefined ? p : null;
  }

  getRelatedPeople(person) {
    const related = [];

    if (person.mother) related.push(person.mother);
    if (person.father) related.push(person.father);

    related.push(...person.children);
    related.push(...person.spouses);
    related.push(...person.siblings);

    return [...new Set(related.filter(Boolean))];
  }

  getConnectedComponents() {
    const visited = new Set();
    const components = [];

    this.persons.forEach((person) => {
      if (visited.has(person.pid)) return;

      const component = [];
      const stack = [person];
      visited.add(person.pid);

      while (stack.length > 0) {
        const current = stack.pop();
        component.push(current);

        this.getRelatedPeople(current).forEach((relatedPerson) => {
          if (visited.has(relatedPerson.pid)) return;
          visited.add(relatedPerson.pid);
          stack.push(relatedPerson);
        });
      }

      components.push(component);
    });

    this.componentSortOrder = new Map(
      components
        .map((component, index) => ({
          index,
          sortKey: Math.min(
            ...component.map((person) => {
              if (typeof person.upn === 'number' && person.upn > 0) {
                return person.upn;
              }

              return typeof person.pid === 'number'
                ? person.pid
                : Number.MAX_SAFE_INTEGER;
            })
          )
        }))
        .sort((a, b) => a.sortKey - b.sortKey)
        .map((entry, orderIndex) => [entry.index, orderIndex])
    );

    components.forEach((component, index) => {
      component.forEach((person) => {
        person.componentId = index;
      });
    });

    return components;
  }

  alignDisconnectedComponentsToTop() {
    const components = this.getConnectedComponents();

    if (components.length <= 1) {
      return;
    }

    const componentMinDepths = components.map((component) =>
      Math.min(...component.map((person) => person.rowDepth))
    );

    const globalMinDepth = Math.min(...componentMinDepths);

    // A proxy clone, and the original it duplicates, are tethered to each other's
    // generation (same person) — neither side's component is a free-floating
    // family, so don't realign either to the global top row (that puts the proxy's
    // spouse a generation too high with a diagonal line — G-1-01376). This covers
    // whichever of the pair ends up disconnected after deriveProxies splits them.
    const proxyOriginals = new Set(
      this.persons
        .filter((p) => p.data?.proxyOf != null)
        .map((p) => p.data.proxyOf)
    );
    components.forEach((component, index) => {
      if (
        component.some(
          (p) => p.data?.proxyOf != null || proxyOriginals.has(p.pid)
        )
      ) {
        return;
      }

      const componentMinDepth = componentMinDepths[index];
      const delta = globalMinDepth - componentMinDepth;

      if (delta === 0) {
        return;
      }

      component.forEach((person) => {
        person.setRowDepthWithoutCausingRecursion(person.rowDepth + delta);
      });
    });

    if (this.persons.length > 0) {
      this.minRowDepth = Math.min(...this.persons.map((p) => p.rowDepth));
      this.maxRowDepth = Math.max(...this.persons.map((p) => p.rowDepth));
    }
  }

  addSpacingBetweenDisconnectedComponents() {
    const components = this.getConnectedComponents();

    if (components.length <= 1) {
      return;
    }

    const minGap = Math.max(1, config.VIRTUAL_X_REQUIRED_BETWEEN_PEOPLE * 1);

    const componentBounds = components
      .map((component) => ({
        component,
        minX: Math.min(...component.map((person) => person.virtualX)),
        maxX: Math.max(...component.map((person) => person.virtualX))
      }))
      .sort((a, b) => a.minX - b.minX);

    let previousMaxX = componentBounds[0].maxX;

    for (let i = 1; i < componentBounds.length; i++) {
      const bounds = componentBounds[i];
      const requiredMinX = previousMaxX + minGap;

      if (bounds.minX < requiredMinX) {
        const delta = requiredMinX - bounds.minX;

        bounds.component.forEach((person) => {
          person.virtualX += delta;
        });

        bounds.minX += delta;
        bounds.maxX += delta;
      }

      previousMaxX = bounds.maxX;
    }
  }
  _ancestorCascadeWouldCollapse(person, delta) {
    const processedIds = new Set();
    const movedPathPids = new Set([person.pid]);

    const collectAncestorPath = (node) => {
      if (!node || movedPathPids.has(node.pid)) return;
      movedPathPids.add(node.pid);
      collectAncestorPath(node.mother);
      collectAncestorPath(node.father);
    };

    collectAncestorPath(person.mother);
    collectAncestorPath(person.father);

    const wouldCollapse = (ancestor) => {
      if (!ancestor || processedIds.has(ancestor.pid)) return false;
      processedIds.add(ancestor.pid);

      const newDepth = ancestor.rowDepth + delta;

      for (const child of ancestor.children) {
        const childDepth = movedPathPids.has(child.pid)
          ? child.rowDepth + delta
          : child.rowDepth;
        if (childDepth <= newDepth) {
          if (child.children.length > 0) {
            return true;
          }
        }
      }

      if (ancestor.mother && wouldCollapse(ancestor.mother)) return true;
      if (ancestor.father && wouldCollapse(ancestor.father)) return true;

      return false;
    };
    if (person.mother && wouldCollapse(person.mother)) return true;
    if (person.father && wouldCollapse(person.father)) return true;
    return false;
  }

  moveAncestorsDown(person, generationDelta) {
    const processedIds = new Set();

    const moveAncestorsRecursive = (p, delta) => {
      if (!p || processedIds.has(p.pid)) return;
      processedIds.add(p.pid);

      // Move mother and father down
      if (p.mother) {
        p.mother.setRowDepthWithoutCausingRecursion(p.mother.rowDepth + delta);
        moveAncestorsRecursive(p.mother, delta);
      }
      if (p.father) {
        p.father.setRowDepthWithoutCausingRecursion(p.father.rowDepth + delta);
        moveAncestorsRecursive(p.father, delta);
      }
    };

    moveAncestorsRecursive(person, generationDelta);
  }

  /**
   * Add person to family
   */
  addPerson(personData) {
    const person = new PedigreePerson(personData.pid, personData);
    person.family = this;
    this.persons.push(person);
    // First insertion wins (mirrors the old find()'s first-match on a duplicate pid).
    if (!this._personByPid.has(person.pid))
      this._personByPid.set(person.pid, person);
    return person;
  }

  getTwinsInSameSubGroup(person) {
    if (!person.belongsToSiblingGroup) return [];

    const siblingGroup = person.belongsToSiblingGroup;

    // Find which sub-group this person belongs to
    for (const subGroup of siblingGroup.subGroups) {
      if (subGroup.peopleInGroup.has(person)) {
        // Check if this is a twin sub-group (not normal)
        if (subGroup.groupType === SIBLING_TYPE.normal) {
          return []; // Not a twin group
        }

        // Return all people in this twin sub-group
        return Array.from(subGroup.peopleInGroup.keys());
      }
    }

    return [];
  }

  /**
   * Every person in this person's sibling group, across all its sub-groups.
   *
   * TGCLOUD-1692: the twin renderer draws a glyph across a run of ADJACENT set members,
   * and "adjacent" has to mean "with nobody drawn between them" — not merely "next in
   * the set". Without the sibship, an ordinary full sibling seated between two twins is
   * invisible to the adjacency test and the bar is drawn straight across them, which is
   * the false-assertion defect this ticket is about.
   */
  getSibshipOf(person) {
    const group = person?.belongsToSiblingGroup;
    if (!group || !Array.isArray(group.subGroups)) return [];
    return group.subGroups.flatMap((sub) =>
      Array.from(sub.peopleInGroup.keys())
    );
  }

  getReducedSpacingForTwins() {
    return config.TWIN_SPACING_MULTIPLIER;
  }

  areTwinsInSameSubGroup(person, nextPerson) {
    if (!person || !nextPerson) return false;

    const twins = this.getTwinsInSameSubGroup(person);
    return twins.length > 1 && twins.includes(nextPerson);
  }

  // Returns true when person and spouse share at least one common ancestor,
  // meaning this is a consanguineous relationship that should not trigger
  // generation re-alignment or ancestor shuffling.
  _areConsanguineous(person, spouse) {
    const getAncestorPids = (p) => {
      const pids = new Set();
      const walk = (node) => {
        if (!node || pids.has(node.pid)) return;
        pids.add(node.pid);
        if (node.mother) walk(node.mother);
        if (node.father) walk(node.father);
      };
      if (p.mother) walk(p.mother);
      if (p.father) walk(p.father);
      return pids;
    };

    const personAncestors = getAncestorPids(person);
    if (personAncestors.has(spouse.pid)) return true;

    const spouseAncestors = getAncestorPids(spouse);
    if (spouseAncestors.has(person.pid)) return true;

    for (const pid of personAncestors) {
      if (spouseAncestors.has(pid)) return true;
    }

    return false;
  }

  // D4: the FREE-FLOATING tower of a married-in spouse S (relative to partner D).
  // BFS from S over parents / spouses / children, where a child joins only when
  // BOTH its parents are inside the set (or null) — so the S-D union's own shared
  // children stay OUT (they belong to both sides and anchor to D's row on their
  // own). Returns the member set when the tower hangs off the pedigree by the S-D
  // marriage ALONE: D is not reachable, and no member has a spouse or co-parent
  // outside the set (the S-D pair itself excepted). Returns null otherwise —
  // e.g. 55-14, where 55's tower is anchored by 29's other marriage.
  _floatingTowerOf(S, D) {
    const set = new Set([S.pid]);
    let grew = true;
    while (grew) {
      grew = false;
      for (const p of this.persons) {
        if (set.has(p.pid)) continue;
        const inSet = (pid) => pid == null || set.has(pid);
        const isParentOfMember = this.persons.some(
          (m) =>
            set.has(m.pid) && (m.fatherId === p.pid || m.motherId === p.pid)
        );
        const isSpouseOfMember =
          p.pid !== D.pid && (p.spouseIds || []).some((sid) => set.has(sid));
        const isFullyInSetChild =
          (p.fatherId != null || p.motherId != null) &&
          inSet(p.fatherId) &&
          inSet(p.motherId);
        if (isParentOfMember || isSpouseOfMember || isFullyInSetChild) {
          if (p.pid === D.pid) return null; // reached the partner: not floating
          set.add(p.pid);
          grew = true;
        }
      }
    }
    // External-anchor check: any spouse or co-parent link from the tower to the
    // outside (other than S-D itself) pins the tower — leave it alone.
    for (const m of this.persons) {
      if (!set.has(m.pid)) continue;
      for (const sid of m.spouseIds || []) {
        if (set.has(sid)) continue;
        if (m.pid === S.pid && sid === D.pid) continue;
        return null;
      }
      for (const c of m.children || []) {
        if (set.has(c.pid)) continue;
        const other = c.fatherId === m.pid ? c.motherId : c.fatherId;
        if (m.pid === S.pid && other === D.pid) continue; // the union's own child
        if (other != null && !set.has(other)) return null;
      }
    }
    return set;
  }

  _alignFreeFloatingInLawTowers() {
    const maxRounds = 20;
    for (let round = 0; round < maxRounds; round++) {
      let shifted = false;
      for (const person of this.persons) {
        for (const spouse of person.spouses) {
          if (person.rowDepth === spouse.rowDepth) continue;
          // Explicit cross-level unions (SetRelation) stay diagonal by intent.
          const crossLevelIds = person.data?.crossLevelSpouseIds;
          if (
            Array.isArray(crossLevelIds) &&
            crossLevelIds.includes(spouse.pid)
          ) {
            continue;
          }
          if (this._areConsanguineous(person, spouse)) continue;
          const [shallower, deeper] =
            person.rowDepth < spouse.rowDepth
              ? [person, spouse]
              : [spouse, person];
          const tower = this._floatingTowerOf(shallower, deeper);
          if (!tower) continue;
          const delta = deeper.rowDepth - shallower.rowDepth;
          for (const pid of tower) {
            const member = this.persons.find((q) => q.pid === pid);
            if (member) {
              member.setRowDepthWithoutCausingRecursion(
                member.rowDepth + delta
              );
            }
          }
          shifted = true;
        }
      }
      if (!shifted) return;
      // A shift can un-pin another pair (chained towers) — go again, bounded.
    }
  }

  calculateGenerationsFromRelationships() {
    if (config.LOG_POSITIONING) {
      console.log('\n=== Phase 1: Generation Assignment ===');
    }

    // Reset all to generation 0
    this.persons.forEach((person) => {
      person.setRowDepthWithoutCausingRecursion(0);
    });

    const phaseAMovedPids = new Set();
    let changed = true;
    let iterations = 0;
    const maxIterations = 100;

    while (changed && iterations < maxIterations) {
      changed = false;
      iterations++;

      this.persons.forEach((person) => {
        // If person has parents, must be in generation after them
        const mother = person.mother;
        const father = person.father;

        if (mother || father) {
          const parentMaxGen = Math.max(
            mother ? mother.rowDepth : -1,
            father ? father.rowDepth : -1
          );

          const expectedGen = parentMaxGen + 1;

          if (person.rowDepth < expectedGen) {
            person.setRowDepthWithoutCausingRecursion(expectedGen);
            phaseAMovedPids.add(person.pid);
            changed = true;
          }
        }

        // If person has children, they must be in next generation
        person.children.forEach((child) => {
          const expectedChildGen = person.rowDepth + 1;

          if (child.rowDepth < expectedChildGen) {
            child.setRowDepthWithoutCausingRecursion(expectedChildGen);
            phaseAMovedPids.add(child.pid);
            changed = true;
          }
        });
      });
    }
    changed = true;
    iterations = 0;

    while (changed && iterations < maxIterations) {
      changed = false;
      iterations++;

      this.persons.forEach((person) => {
        person.spouses.forEach((spouse) => {
          const crossLevelIds = person.data?.crossLevelSpouseIds;
          if (
            Array.isArray(crossLevelIds) &&
            crossLevelIds.includes(spouse.pid)
          ) {
            return;
          }

          if (spouse.rowDepth === person.rowDepth) {
            return;
          }

          // Case 1: Spouse has deeper generation, align person to spouse
          if (spouse.rowDepth > person.rowDepth) {
            const generationDelta = spouse.rowDepth - person.rowDepth;

            // If person has no parents move them down
            if (!person.hasParents) {
              person.setRowDepthWithoutCausingRecursion(spouse.rowDepth);
              changed = true;
            } else if (
              spouse.hasParents &&
              !this._areConsanguineous(person, spouse) &&
              !phaseAMovedPids.has(person.pid)
            ) {
              if (
                !this._ancestorCascadeWouldCollapse(person, generationDelta)
              ) {
                person.setRowDepthWithoutCausingRecursion(spouse.rowDepth);
                this.moveAncestorsDown(person, generationDelta);
                changed = true;
              }
            }
          }
          // Case 2: Person has deeper generation, align spouse to person.
          else if (
            person.rowDepth > spouse.rowDepth &&
            !phaseAMovedPids.has(person.pid)
          ) {
            const generationDelta = person.rowDepth - spouse.rowDepth;

            if (!spouse.hasParents) {
              spouse.setRowDepthWithoutCausingRecursion(person.rowDepth);
              changed = true;
            } else if (!this._areConsanguineous(person, spouse)) {
              if (
                !this._ancestorCascadeWouldCollapse(spouse, generationDelta)
              ) {
                spouse.setRowDepthWithoutCausingRecursion(person.rowDepth);
                this.moveAncestorsDown(spouse, generationDelta);
                changed = true;
              }
            }
          }
        });

        // Re-enforce parent/child constraints after any spouse move
        const mother = person.mother;
        const father = person.father;
        if (mother || father) {
          const parentMaxGen = Math.max(
            mother ? mother.rowDepth : -1,
            father ? father.rowDepth : -1
          );
          const expectedGen = parentMaxGen + 1;
          if (person.rowDepth < expectedGen) {
            person.setRowDepthWithoutCausingRecursion(expectedGen);
            changed = true;
          }
        }
        person.children.forEach((child) => {
          const expectedChildGen = person.rowDepth + 1;
          if (child.rowDepth < expectedChildGen) {
            child.setRowDepthWithoutCausingRecursion(expectedChildGen);
            changed = true;
          }
        });
      });
    }

    // D4 (G-19-01458): align FREE-FLOATING in-law towers by a single deterministic
    // component shift BEFORE the equalisation loop + its snapshot. Loop 3 below would
    // align these too, but when the SAME family also contains an unsatisfiable
    // cross-level pair (e.g. 55-14, both partners pinned by blood links), loop 3
    // ping-pongs to the cap and its all-or-nothing REVERT used to throw the
    // satisfiable alignments away with it — leaving e.g. spouse 47 (tower 48/49)
    // a row above his wife 12 and drawing a long diagonal through other subtrees.
    // A provably-floating tower needs no iteration (one rigid shift, no cascade),
    // so aligning it here is convergence-safe and survives the revert.
    this._alignFreeFloatingInLawTowers();

    // Snapshot before the 3rd (spouse-equalisation) loop. Unlike loop 2 it is
    // UNCONDITIONAL, and on an UNSATISFIABLE cross-level cycle — two siblings married
    // into families one generation apart (G-19, config 6a2fe978) — it ping-pongs to
    // the iteration cap, inflating depths and relocating founder couples below their
    // own grandchildren. Loops 1+2 already produced the correct rows, so if this loop
    // fails to converge we revert to this snapshot (below), leaving the unsatisfiable
    // pair as a cross-level diagonal. V3 only; satisfiable families converge in a few
    // iterations and never revert, so they are byte-for-byte unaffected.
    const preEqualiseDepths = config.ENABLE_LAYOUT_V3
      ? new Map(this.persons.map((p) => [p.pid, p.rowDepth]))
      : null;

    changed = true;
    iterations = 0;

    while (changed && iterations < maxIterations) {
      changed = false;
      iterations++;

      this.persons.forEach((person) => {
        person.spouses.forEach((spouse) => {
          const crossLevelIds = person.data?.crossLevelSpouseIds;
          if (
            Array.isArray(crossLevelIds) &&
            crossLevelIds.includes(spouse.pid)
          ) {
            return;
          }

          if (person.rowDepth === spouse.rowDepth) return;

          const [shallower, deeper] =
            person.rowDepth < spouse.rowDepth
              ? [person, spouse]
              : [spouse, person];

          const delta = deeper.rowDepth - shallower.rowDepth;

          if (this._areConsanguineous(shallower, deeper)) return;
          shallower.setRowDepthWithoutCausingRecursion(deeper.rowDepth);
          this.moveAncestorsDown(shallower, delta);

          changed = true;
        });
      });

      this.persons.forEach((person) => {
        const mother = person.mother;
        const father = person.father;
        if (mother || father) {
          const parentMaxGen = Math.max(
            mother ? mother.rowDepth : -1,
            father ? father.rowDepth : -1
          );
          const expectedGen = parentMaxGen + 1;
          if (person.rowDepth < expectedGen) {
            person.setRowDepthWithoutCausingRecursion(expectedGen);
            changed = true;
          }
        }
        person.children.forEach((child) => {
          const expectedChildGen = person.rowDepth + 1;
          if (child.rowDepth < expectedChildGen) {
            child.setRowDepthWithoutCausingRecursion(expectedChildGen);
            changed = true;
          }
        });
      });
    }

    // The 3rd loop exits with changed=true ONLY when it hit the iteration cap
    // without converging — an unsatisfiable cross-level cycle ran it away. Revert to
    // the pre-loop rows (loops 1+2 were already correct; see the snapshot note).
    if (preEqualiseDepths && changed) {
      for (const p of this.persons) {
        const d = preEqualiseDepths.get(p.pid);
        if (d != null) p.setRowDepthWithoutCausingRecursion(d);
      }
    }

    // A proxy clone and the original it duplicates are the SAME person, so when
    // one of the pair ends up FLOATING they must share a generation. deriveProxies
    // nulls the clone's parents and moves the split-off marriage to one of the
    // pair, so EITHER the clone OR the original can end up the floating one
    // stranded at generation 0 — and its married-in spouse then renders a
    // generation too high with a diagonal line (G-1-01376). Resolve each such
    // pair to the DEEPER of the two rows (the one the tree actually anchors),
    // then pull the parent-less married-in spouses of both onto it.
    //
    // BUT only when one side genuinely lacks its own generation anchor (no
    // parents and no spouse with parents). A CROSS-GENERATIONAL proxy — the
    // feature's whole purpose — legitimately straddles rows: the original sits
    // with its sibship and the clone sits beside its spouse a row away.
    // Unconditionally forcing max() dragged the clone down onto its own
    // children's generation, wedging it inside their sibship (G-1-01411).
    // Gated on the presence of proxies — a strict no-op for every proxy-free
    // family.
    if (this.persons.some((p) => p.data?.proxyOf != null)) {
      const byPid = new Map(this.persons.map((p) => [p.pid, p]));
      const hasOwnAnchor = (node) =>
        node.hasParents || node.spouses.some((s) => s.hasParents);
      changed = true;
      iterations = 0;
      while (changed && iterations < maxIterations) {
        changed = false;
        iterations++;
        this.persons.forEach((person) => {
          if (person.data?.proxyOf == null) return;
          const orig = byPid.get(person.data.proxyOf);
          if (!orig) return;
          if (hasOwnAnchor(person) && hasOwnAnchor(orig)) return;
          const gen = Math.max(person.rowDepth, orig.rowDepth);
          for (const node of [person, orig]) {
            if (node.rowDepth !== gen) {
              node.setRowDepthWithoutCausingRecursion(gen);
              changed = true;
            }
            // A married-in spouse (no parents of its own) follows onto that row.
            node.spouses.forEach((spouse) => {
              if (!spouse.hasParents && spouse.rowDepth !== gen) {
                spouse.setRowDepthWithoutCausingRecursion(gen);
                changed = true;
              }
            });
          }
        });
      }
    }

    // Update min/max
    if (this.persons.length > 0) {
      this.minRowDepth = Math.min(...this.persons.map((p) => p.rowDepth));
      this.maxRowDepth = Math.max(...this.persons.map((p) => p.rowDepth));
    }
  }

  createSiblingGroups() {
    this.siblingGroups = [];
    const processedPersonIds = new Set();

    this.persons.forEach((person) => {
      if (processedPersonIds.has(person.pid)) return;

      // Get all siblings (same mother AND father)
      const siblings = this.persons.filter((p) => {
        // Same person
        if (p.pid === person.pid) return true;

        // Both parents must match
        const sameMother = person.motherId === p.motherId;
        const sameFather = person.fatherId === p.fatherId;

        return (
          sameMother &&
          sameFather &&
          (person.motherId !== null || person.fatherId !== null)
        );
      });

      if (siblings.length > 1) {
        // Create sibling group
        const siblingGroup = new PedigreeSiblingGroup(this);

        siblings.forEach((sibling) => {
          siblingGroup.addPersonToGroup(sibling);
          processedPersonIds.add(sibling.pid);
        });

        // Group siblings by twinSubGroupId for twin/triplet sets
        const twinGroups = new Map();
        const normalSiblings = [];

        siblings.forEach((sibling) => {
          if (sibling.twinSubGroupId) {
            // Has twin group ID - add to twin group
            if (!twinGroups.has(sibling.twinSubGroupId)) {
              twinGroups.set(sibling.twinSubGroupId, []);
            }
            twinGroups.get(sibling.twinSubGroupId).push(sibling);
          } else {
            // Normal sibling without twin group
            normalSiblings.push(sibling);
          }
        });

        // Create sub-groups for each twin set
        twinGroups.forEach((twinsInGroup) => {
          const subGroup =
            PedigreeSiblingSubGroup.createNewSubGroup(siblingGroup);
          // Set sub-group type from first twin's siblingType
          subGroup.groupType = twinsInGroup[0].siblingType || SIBLING_TYPE.twin;

          twinsInGroup.forEach((twin) => {
            subGroup.peopleInGroup.set(twin, twin.siblingType);
          });

          if (config.LOG_POSITIONING) {
            const ids = twinsInGroup.map((t) => t.pid).join(', ');
            console.log(`  Twin Sub-Group (${subGroup.groupType}): [${ids}]`);
          }
        });

        // Create "normal" sub-group for non-twin siblings
        if (normalSiblings.length > 0) {
          const subGroup =
            PedigreeSiblingSubGroup.createNewSubGroup(siblingGroup);
          subGroup.groupType = SIBLING_TYPE.normal;

          normalSiblings.forEach((sibling) => {
            subGroup.peopleInGroup.set(sibling, SIBLING_TYPE.normal);
          });

          if (config.LOG_POSITIONING) {
            const ids = normalSiblings.map((s) => s.pid).join(', ');
            console.log(`  Normal Sub-Group: [${ids}]`);
          }
        }
      }
    });
  }

  assignDefaultVirtualX(rowDepthGroups) {
    rowDepthGroups.forEach((generation, genIndex) => {
      let currentX = 0;

      generation.forEach((person, personIndex) => {
        person.virtualX = currentX;
        person.virtualY = genIndex;

        // Calculate spacing to next person
        if (personIndex < generation.length - 1) {
          const nextPerson = generation[personIndex + 1];

          // Check if next person is twin in same sub-group
          if (this.areTwinsInSameSubGroup(person, nextPerson)) {
            // Use reduced spacing for twins
            currentX += this.getReducedSpacingForTwins();
          } else {
            // Use normal spacing
            currentX += 1;
          }
        }
      });
    });
  }

  buildSkipList(rowDepthGroups) {
    const skipList = [];

    this.persons.forEach((person) => {
      if (person.children.length === 0) {
        skipList.push(person);
        return;
      }

      const hasSpouseOnRight = person.spouses.some(
        (spouse) => person.virtualX < spouse.virtualX
      );

      if (!hasSpouseOnRight && !person.isSingle) {
        skipList.push(person);
        return;
      }

      const children = person.children;
      const leftMostChild = children.reduce((min, c) =>
        c.virtualX < min.virtualX ? c : min
      );

      const personGen = rowDepthGroups.findIndex((gen) => gen.includes(person));
      const childGen = rowDepthGroups.findIndex((gen) =>
        gen.includes(leftMostChild)
      );

      if (personGen !== -1 && childGen !== -1 && childGen !== personGen + 1) {
        skipList.push(person);
        return;
      }

      if (personGen !== -1) {
        const personGeneration = rowDepthGroups[personGen];
        const personIndex = personGeneration.indexOf(person);

        for (let i = 0; i < personIndex; i++) {
          const leftPerson = personGeneration[i];

          if (person.spouses.includes(leftPerson)) continue;

          const leftPersonChildren = leftPerson.children;
          if (leftPersonChildren.length === 0) continue;

          const leftPersonLeftMostChild = leftPersonChildren.reduce((min, c) =>
            c.virtualX < min.virtualX ? c : min
          );

          if (leftMostChild.virtualX < leftPersonLeftMostChild.virtualX) {
            const rightParent = leftPersonLeftMostChild.rightParent;
            if (!rightParent || rightParent.virtualX < person.virtualX) {
              skipList.push(person);
              return;
            }
          }
        }
      }
    });

    return skipList;
  }

  calculateRightDistancesBetweenPeople(rowDepthGroups) {
    // Build skip list (people not to process)
    const skipSet = new Set(this.buildSkipList(rowDepthGroups));

    let iterationCount = 0;
    const maxIterations = config.MAX_ITERATIONS_FOR_FIX_PARENTS || 50;

    while (iterationCount < maxIterations) {
      iterationCount++;
      let wasAnyPersonMoved = false;

      // Process generations from top to bottom
      for (
        let generation = 0;
        generation < rowDepthGroups.length;
        generation++
      ) {
        const currentGen = rowDepthGroups[generation];

        for (const person of currentGen) {
          if (skipSet.has(person)) continue;

          // Handle single parent case (no spouses)
          if (person.isSingle && person.children.length > 0) {
            const allChildren = person.getAllChildrenWithSpouse(null);
            if (allChildren.length === 0) continue;

            const sortedChildren = [...allChildren].sort(
              (a, b) => a.virtualX - b.virtualX
            );
            const leftChild = sortedChildren[0];
            const rightChild = sortedChildren[sortedChildren.length - 1];
            const isSingleChild = leftChild === rightChild;

            // SCENARIO 1: Single Parent, Single Child
            if (isSingleChild) {
              if (person.virtualX < leftChild.virtualX) {
                const deltaX = leftChild.virtualX - person.virtualX;
                this.movePersonToRight(person, deltaX, currentGen);
                wasAnyPersonMoved = true;
              } else if (leftChild.virtualX < person.virtualX) {
                const deltaX = person.virtualX - leftChild.virtualX;
                const childGen = rowDepthGroups[generation + 1];
                if (childGen) {
                  this.movePersonToRight(leftChild, deltaX, childGen);
                  wasAnyPersonMoved = true;
                }
              }
            }
            // SCENARIO 2: Single Parent, Multiple Children
            else {
              const centerOfChildren =
                (leftChild.virtualX + rightChild.virtualX) / 2;

              if (person.virtualX < centerOfChildren) {
                const deltaX = centerOfChildren - person.virtualX;
                this.movePersonToRight(person, deltaX, currentGen);
                wasAnyPersonMoved = true;
              } else if (centerOfChildren < person.virtualX) {
                const deltaX = person.virtualX - centerOfChildren;
                const childGen = rowDepthGroups[generation + 1];
                if (childGen) {
                  this.movePersonToRight(leftChild, deltaX, childGen);
                  wasAnyPersonMoved = true;
                }
              }
            }
            continue;
          }

          // Process EACH spouse separately for multiple spouse support
          // Sort spouses by virtualX to process from left to right
          const spousesOnRight = person.spouses
            .filter((s) => s.virtualX > person.virtualX)
            .sort((a, b) => a.virtualX - b.virtualX);

          for (const spouse of spousesOnRight) {
            // Get children with this specific spouse
            const childrenWithSpouse = person.getAllChildrenWithSpouse(spouse);
            if (childrenWithSpouse.length === 0) continue;

            const sortedChildren = [...childrenWithSpouse].sort(
              (a, b) => a.virtualX - b.virtualX
            );
            const leftChild = sortedChildren[0];
            const rightChild = sortedChildren[sortedChildren.length - 1];
            const isSingleChild = leftChild === rightChild;

            const centerOfParents = (person.virtualX + spouse.virtualX) / 2;

            // SCENARIO 3: Multiple Parents, Single Child
            if (isSingleChild) {
              if (leftChild.virtualX < centerOfParents) {
                const deltaX = centerOfParents - leftChild.virtualX;
                const childGen = rowDepthGroups[generation + 1];
                if (childGen) {
                  this.movePersonToRight(leftChild, deltaX, childGen);
                  wasAnyPersonMoved = true;
                }
              } else if (centerOfParents < leftChild.virtualX) {
                const deltaX = leftChild.virtualX - centerOfParents;
                this.movePersonToRight(person, deltaX, currentGen);
                wasAnyPersonMoved = true;
              }
            }
            // SCENARIO 4: Multiple Parents, Multiple Children
            else {
              const centerOfChildren =
                (leftChild.virtualX + rightChild.virtualX) / 2;
              const parentWidth = spouse.virtualX - person.virtualX;
              const childrenWidth = rightChild.virtualX - leftChild.virtualX;

              if (childrenWidth <= parentWidth) {
                // Children narrower than parents - center children under parents
                if (centerOfChildren < centerOfParents) {
                  const deltaX = centerOfParents - centerOfChildren;
                  const childGen = rowDepthGroups[generation + 1];
                  if (childGen) {
                    this.movePersonToRight(leftChild, deltaX, childGen);
                    wasAnyPersonMoved = true;
                  }
                } else if (centerOfParents < centerOfChildren) {
                  const deltaX = centerOfChildren - centerOfParents;
                  this.movePersonToRight(person, deltaX, currentGen);
                  wasAnyPersonMoved = true;
                }
              } else {
                // Children wider than parents - center parents above children
                if (centerOfParents !== centerOfChildren) {
                  const deltaX = centerOfChildren - centerOfParents;
                  this.movePersonToRight(person, deltaX, currentGen);
                  wasAnyPersonMoved = true;
                }
              }
            }
          }
        }
      }

      if (!wasAnyPersonMoved) break;
    }
  }

  movePersonToRight(personToBeMoved, deltaX, generation) {
    // skip if deltaX is NaN or 0
    if (isNaN(deltaX) || Math.abs(deltaX) < 0.01) {
      return false;
    }

    const maxDelta = config.MAX_MOVEMENT_DELTA || 10;
    if (Math.abs(deltaX) > maxDelta) {
      deltaX = deltaX > 0 ? maxDelta : -maxDelta;
    }

    // Find index of person in generation
    const personIndex = generation.indexOf(personToBeMoved);
    if (personIndex === -1) {
      return false;
    }

    if (deltaX > 0) {
      const minGap = config.VIRTUAL_X_REQUIRED_BETWEEN_PEOPLE;
      personToBeMoved.virtualX += deltaX;
      for (let i = personIndex + 1; i < generation.length; i++) {
        const prev = generation[i - 1];
        const curr = generation[i];
        const overlap = prev.virtualX + minGap - curr.virtualX;
        if (overlap > 0.001) {
          curr.virtualX += overlap;
        } else {
          break;
        }
      }
    } else {
      const minGap = config.VIRTUAL_X_REQUIRED_BETWEEN_PEOPLE;
      personToBeMoved.virtualX += deltaX;
      for (let i = personIndex - 1; i >= 0; i--) {
        const next = generation[i + 1];
        const curr = generation[i];
        const overlap = next.virtualX - minGap - curr.virtualX;
        if (overlap < -0.001) {
          curr.virtualX += overlap;
        } else {
          break;
        }
      }
    }

    return true;
  }

  // eslint-disable-next-line no-unused-vars
  fixPositionOfParents(_rowDepthGroups) {
    let iterationNum = 0;

    while (iterationNum < config.MAX_ITERATIONS_FOR_FIX_PARENTS) {
      iterationNum++;
      let anyMoveSuccessful = false;

      const parentsToProcess = this.persons
        .filter((p) => p.isParent)
        .sort((a, b) => a.rowDepth - b.rowDepth);

      for (const person of parentsToProcess) {
        // Get all spouses to process (including null for single parent)
        const spousesToProcess = person.isSingle ? [null] : person.spouses;

        for (const spouse of spousesToProcess) {
          const leftMostChild = person.getLeftMostChildWithSpouse(spouse);
          const rightMostChild = person.getRightMostChildWithSpouse(spouse);

          if (!leftMostChild || !rightMostChild) continue;

          const allChildren = person.getAllChildrenWithSpouse(spouse);
          if (allChildren.length === 0) continue;

          let centerOfParents;
          let parentWidth;

          if (spouse) {
            centerOfParents = (person.virtualX + spouse.virtualX) / 2;
            parentWidth = Math.abs(spouse.virtualX - person.virtualX);
          } else {
            centerOfParents = person.virtualX;
            parentWidth = 0;
          }

          const centerOfChildren =
            (leftMostChild.virtualX + rightMostChild.virtualX) / 2;
          const childrenWidth =
            rightMostChild.virtualX - leftMostChild.virtualX;

          if (childrenWidth <= parentWidth) {
            if (centerOfChildren < centerOfParents) {
              const deltaX = centerOfParents - centerOfChildren;

              const moveSuccessful = this.tryMovingPersonSimple(
                leftMostChild,
                leftMostChild.virtualX + deltaX,
                null,
                []
              );

              if (moveSuccessful) {
                anyMoveSuccessful = true;
              }
            } else if (centerOfParents < centerOfChildren) {
              const deltaX = centerOfChildren - centerOfParents;

              const moveSuccessful = this.tryMovingPersonSimple(
                person,
                person.virtualX + deltaX,
                spouse,
                allChildren
              );

              if (moveSuccessful) {
                anyMoveSuccessful = true;
              }
            }
          } else {
            // Children are wider than parents - move parents to center above children
            if (centerOfParents !== centerOfChildren) {
              const deltaX = centerOfChildren - centerOfParents;

              const moveSuccessful = this.tryMovingPersonSimple(
                person,
                person.virtualX + deltaX,
                spouse,
                allChildren
              );

              if (moveSuccessful) {
                anyMoveSuccessful = true;
              } else if (centerOfParents > centerOfChildren) {
                // If we couldn't move parents, try moving children instead
                const childDeltaX = centerOfParents - centerOfChildren;
                const childMoveSuccessful = this.tryMovingPersonSimple(
                  leftMostChild,
                  leftMostChild.virtualX + childDeltaX,
                  null,
                  []
                );

                if (childMoveSuccessful) {
                  anyMoveSuccessful = true;
                }
              }
            }
          }
        }
      }

      if (!anyMoveSuccessful) break;
    }
  }

  tryMovingPersonSimple(person, targetX, excludeSpouse, excludeChildren) {
    const currentX = person.virtualX;
    const movementOffset = Math.abs(targetX - currentX);
    const movementDirection = targetX > currentX ? 'Right' : 'Left';

    // Build shouldNotBeMoved list
    const shouldNotBeMoved = new Set();
    if (excludeSpouse) shouldNotBeMoved.add(excludeSpouse);
    if (excludeChildren)
      excludeChildren.forEach((c) => shouldNotBeMoved.add(c));

    // Evaluate if move is possible
    const whoseMovementIsBeingEvaluated = new Set();
    const canBeMoved = new Set();
    const cannotBeMoved = new Set();

    const isPossible = this.canPersonBeMoved(
      person,
      movementOffset,
      movementDirection,
      whoseMovementIsBeingEvaluated,
      canBeMoved,
      cannotBeMoved,
      shouldNotBeMoved
    );

    if (!isPossible) {
      return false;
    }

    canBeMoved.forEach((p) => {
      if (movementDirection === 'Left') {
        p.virtualX -= movementOffset;
      } else {
        p.virtualX += movementOffset;
      }
    });

    return true;
  }

  canPersonBeMoved(
    person,
    movementOffset,
    movementDirection,
    whoseMovementIsBeingEvaluated,
    canBeMoved,
    cannotBeMoved,
    shouldNotBeMoved
  ) {
    const minGap = config.VIRTUAL_X_REQUIRED_BETWEEN_PEOPLE;

    // Check if already evaluated
    if (canBeMoved.has(person)) {
      return true;
    }
    if (shouldNotBeMoved.has(person)) {
      return false;
    }
    if (cannotBeMoved.has(person)) {
      return false;
    }

    if (!whoseMovementIsBeingEvaluated.has(person)) {
      whoseMovementIsBeingEvaluated.add(person);
    }

    // Check boundary conditions
    if (movementDirection === 'Left') {
      if (person.virtualX < minGap) {
        cannotBeMoved.add(person);
        return false;
      }
    }

    // Check neighbor in movement direction
    const neighbor =
      movementDirection === 'Left'
        ? person.leftNeighbour
        : person.rightNeighbour;

    if (neighbor) {
      let neighborNewX = neighbor.virtualX;

      // If neighbor is also being moved, account for that
      if (
        whoseMovementIsBeingEvaluated.has(neighbor) ||
        canBeMoved.has(neighbor)
      ) {
        if (movementDirection === 'Left') {
          neighborNewX -= movementOffset;
        } else {
          neighborNewX += movementOffset;
        }
      }

      const gap = Math.abs(person.virtualX - neighborNewX);

      if (gap - movementOffset < minGap) {
        // Neighbor blocks this move, try moving neighbor too
        const neighborCanMove = this.canPersonBeMoved(
          neighbor,
          movementOffset,
          movementDirection,
          whoseMovementIsBeingEvaluated,
          canBeMoved,
          cannotBeMoved,
          shouldNotBeMoved
        );

        if (!neighborCanMove) {
          cannotBeMoved.add(person);
          return false;
        }
      }
    }

    if (movementDirection === 'Left') {
      const peopleToRight = this.persons
        .filter(
          (p) => p.rowDepth === person.rowDepth && p.virtualX > person.virtualX
        )
        .sort((a, b) => a.virtualX - b.virtualX);

      for (const rightPerson of peopleToRight) {
        if (shouldNotBeMoved.has(rightPerson)) {
          continue;
        }

        if (whoseMovementIsBeingEvaluated.has(rightPerson)) {
          continue;
        }

        const rightCanMove = this.canPersonBeMoved(
          rightPerson,
          movementOffset,
          movementDirection,
          whoseMovementIsBeingEvaluated,
          canBeMoved,
          cannotBeMoved,
          shouldNotBeMoved
        );

        if (!rightCanMove) {
          cannotBeMoved.add(person);
          return false;
        }
      }
    }

    canBeMoved.add(person);
    return true;
  }

  squeezeGenerationsHorizontally(rowDepthGroups) {
    let iterationNum = 0;

    while (iterationNum < config.MAX_ITERATIONS_FOR_SQUEEZE) {
      iterationNum++;
      let anyMoved = false;

      for (
        let genIndex = rowDepthGroups.length - 1;
        genIndex >= 0;
        genIndex--
      ) {
        const generation = rowDepthGroups[genIndex];

        const sortedPeople = [...generation].sort(
          (a, b) => b.virtualX - a.virtualX
        );

        for (const person of sortedPeople) {
          const targetX = person.virtualX - 0.5;

          if (targetX < 0) continue;

          if (person.children.length > 0) {
            const childrenX = person.children
              .map((c) => c.virtualX)
              .filter((x) => !isNaN(x));
            if (childrenX.length > 0) {
              const leftMostChild = Math.min(...childrenX);
              const rightMostChild = Math.max(...childrenX);
              const childrenCenter = (leftMostChild + rightMostChild) / 2;

              // If moving left would move us away from children center, skip
              if (
                targetX < childrenCenter &&
                person.virtualX >= childrenCenter
              ) {
                continue;
              }
            }
          }

          const leftNeighbor = person.leftNeighbour;

          if (!leftNeighbor || person.virtualX - leftNeighbor.virtualX > 1.5) {
            const moved = this.tryMovingPersonSimple(person, targetX, null, []);

            if (moved) {
              anyMoved = true;
            }
          }
        }
      }

      if (!anyMoved) break;
    }
  }

  removeAllEmptyRangesOfX() {
    const allX = [...new Set(this.persons.map((p) => p.virtualX))].sort(
      (a, b) => a - b
    );

    const gaps = [];

    // Find gaps larger than required minimum
    for (let i = 0; i < allX.length - 1; i++) {
      const currentX = allX[i];
      const nextX = allX[i + 1];
      const gap = nextX - currentX;
      const requiredGap = config.VIRTUAL_X_REQUIRED_BETWEEN_PEOPLE;

      // If gap is larger than required + threshold, it's removable
      if (gap > requiredGap + 0.5) {
        gaps.push({
          start: currentX,
          excessGap: gap - requiredGap
        });
      }
    }

    // Close gaps from right to left (to avoid cumulative shifting issues)
    gaps.reverse().forEach((gap) => {
      // Shift all people to the right of this gap leftward
      this.persons.forEach((person) => {
        if (person.virtualX > gap.start) {
          person.virtualX -= gap.excessGap;
        }
      });
    });
  }

  bringMotherFatherCloserToEachOther() {
    let iterationNum = 0;

    while (iterationNum < config.MAX_ITERATIONS_FOR_BRING_PARENTS_CLOSER) {
      iterationNum++;
      let anyMoveSuccessful = false;

      // Process sibling groups
      for (const siblingGroup of this.siblingGroups) {
        const moved = this.tryMovingParentsCloser(siblingGroup.leftMostSibling);
        if (moved) anyMoveSuccessful = true;
      }

      // Process single children with both parents (not in sibling groups)
      for (const person of this.persons) {
        if (person.belongsToSiblingGroup) continue;

        const moved = this.tryMovingParentsCloser(person);
        if (moved) anyMoveSuccessful = true;
      }

      if (!anyMoveSuccessful) break;
    }
  }

  tryMovingParentsCloser(child) {
    const leftParent = child.leftParent;
    const rightParent = child.rightParent;

    if (!leftParent || !rightParent || leftParent === rightParent) {
      return false;
    }

    // Check that left and right parent are actually spouses of each other
    // (they can still have other spouses - we support multiple spouses)
    const areSpouses =
      leftParent.spouseIds.includes(rightParent.pid) &&
      rightParent.spouseIds.includes(leftParent.pid);

    if (!areSpouses) {
      return false;
    }

    // Check if there are other people (not spouses of either parent) between them
    const peopleBetweenParents = this.persons.filter(
      (p) =>
        p.rowDepth === leftParent.rowDepth &&
        p.virtualX > leftParent.virtualX &&
        p.virtualX < rightParent.virtualX &&
        !leftParent.spouseIds.includes(p.pid) &&
        !rightParent.spouseIds.includes(p.pid)
    );

    // If there are non-spouse people between parents, don't move
    if (peopleBetweenParents.length > 0) {
      return false;
    }

    const currentGap = rightParent.virtualX - leftParent.virtualX;

    if (currentGap <= config.VIRTUAL_X_REQUIRED_BETWEEN_PARENTS) {
      return false;
    }

    const targetGap = config.VIRTUAL_X_REQUIRED_BETWEEN_PARENTS;
    const offsetPerParent = (currentGap - targetGap) / 2;

    const leftTargetX = leftParent.virtualX + offsetPerParent;
    const rightTargetX = rightParent.virtualX - offsetPerParent;

    const snapshot = new Map(this.persons.map((p) => [p, p.virtualX]));

    const leftMoved = this.tryMovingPersonSimple(
      leftParent,
      leftTargetX,
      rightParent,
      []
    );

    const rightMoved = this.tryMovingPersonSimple(
      rightParent,
      rightTargetX,
      leftParent,
      []
    );

    if (!leftMoved || !rightMoved) {
      snapshot.forEach((x, p) => {
        p.virtualX = x;
      });
      return false;
    }

    return true;
  }

  convertToCoordinates() {
    const positions = new Map();

    // Group by generation
    const generations = {};
    this.persons.forEach((person) => {
      const gen = person.virtualY;
      if (!generations[gen]) generations[gen] = [];
      generations[gen].push(person);
    });

    const sortedGenKeys = Object.keys(generations).sort((a, b) => a - b);

    // PER-GENERATION VERTICAL SPACING (TGCLOUD-1610). Auto-spacing hands us the
    // vertical space each NODE's labels need (already clamped); the gap BELOW a
    // row is the largest need on that row, floored at BASE. Every generation
    // used to inherit one global LEVEL_HEIGHT — the tallest label block in the
    // family — so a single label-dense node padded out the entire chart.
    //
    // Inactive (flag off, manual spacing, or no needs shipped — which is every
    // engine test) this walk is exactly the old one.
    const needs =
      config.ENABLE_PER_GENERATION_LEVEL_HEIGHT && config.AUTO_ROW_NEEDS
        ? config.AUTO_ROW_NEEDS
        : null;
    this._rowTable = null;
    let gapBelow = null;
    if (needs) {
      gapBelow = sortedGenKeys.map((genKey) => {
        let g = BASE_LEVEL_HEIGHT;
        for (const person of generations[genKey]) {
          const n = needs[String(person.pid)];
          if (n != null && n > g) g = n;
        }
        return g;
      });
    }

    let lastGenerationMaxOffsetY = config.PEDIGREE_OFFSET;
    const ys = [];
    const idxByGen = new Map();

    sortedGenKeys.forEach((genKey, i) => {
      const generation = generations[genKey];

      // Labels hang DOWNWARD from their row, so the pad above the TOP row is
      // never label-driven — only the gaps between rows are.
      const offsetY =
        lastGenerationMaxOffsetY +
        (needs
          ? i === 0
            ? BASE_LEVEL_HEIGHT
            : gapBelow[i - 1]
          : config.LEVEL_HEIGHT);

      generation.forEach((person) => {
        const gridCell = config.GRID_SIZE * 2;
        const offsetX = person.virtualX * config.NODE_GAP + gridCell;

        person.x = offsetX;
        person.y = offsetY;

        positions.set(person.pid, {
          x: offsetX,
          y: offsetY,
          virtualX: person.virtualX,
          virtualY: person.virtualY
        });
      });

      lastGenerationMaxOffsetY = offsetY;
      if (needs) {
        ys[i] = offsetY;
        idxByGen.set(Number(genKey), i);
      }
    });

    if (needs) this._rowTable = { ys, gapBelow, idxByGen };

    return positions;
  }

  // The gap between this generation's row and the one BELOW it. Falls back to
  // the single global pitch whenever per-generation spacing is inactive, so
  // every caller degrades to the exact expression it used before.
  rowGapBelow(gen) {
    const t = this._rowTable;
    const i = t ? t.idxByGen.get(gen) : undefined;
    return i == null ? config.LEVEL_HEIGHT : t.gapBelow[i];
  }

  // The gap between this generation's row and the one ABOVE it — for the top
  // row, the pad between it and the canvas origin.
  rowGapAbove(gen) {
    const t = this._rowTable;
    const i = t ? t.idxByGen.get(gen) : undefined;
    if (i == null) return config.LEVEL_HEIGHT;
    return i === 0 ? t.ys[0] - config.PEDIGREE_OFFSET : t.ys[i] - t.ys[i - 1];
  }

  // The laid-out row positions a pin resolves against (see rowUnits.js). Null
  // when rows are uniform, which is the signal to use the legacy scalar math.
  rowsForPins() {
    const t = this._rowTable;
    return t
      ? { ys: t.ys, gapBelowLast: t.gapBelow[t.gapBelow.length - 1] }
      : null;
  }

  // note; this is a temp solution, this is being hanlded only for single unknow person, we have to add
  // methods to indentify and place the disconnected famliy is the same way
  getUnknownIsolatedPersons() {
    return this.persons.filter(
      (p) =>
        p.data.relationship === 'otherAffected' &&
        !p.motherId &&
        !p.fatherId &&
        p.spouseIds.length === 0 &&
        p.childrenIds.length === 0
    );
  }

  finalizeUnknownPersonPositions() {
    const unknowns = this.getUnknownIsolatedPersons();
    if (unknowns.length === 0) return;

    const nonUnknowns = this.persons.filter((p) => !unknowns.includes(p));

    // Use the minimum virtualY of the actual family members as the top row
    const minVirtualY =
      nonUnknowns.length > 0
        ? Math.min(...nonUnknowns.map((p) => p.virtualY))
        : 0;

    const topRowOthers = nonUnknowns.filter((p) => p.virtualY === minVirtualY);

    let maxX =
      topRowOthers.length > 0
        ? Math.max(...topRowOthers.map((p) => p.virtualX))
        : -1;

    unknowns.forEach((p) => {
      maxX += 1;
      p.virtualX = maxX;
      p.virtualY = minVirtualY;
    });
  }

  equalizeFirstTwoSpouseDistances() {
    const processed = new Set();

    for (const person of this.persons) {
      if (person.spouseIds.length < 2) continue;
      if (processed.has(person.pid)) continue;

      // Get first two spouses
      const spouse1 = this.persons.find((p) => p.pid === person.spouseIds[0]);
      const spouse2 = this.persons.find((p) => p.pid === person.spouseIds[1]);

      if (!spouse1 || !spouse2) continue;
      if (
        spouse1.rowDepth !== person.rowDepth ||
        spouse2.rowDepth !== person.rowDepth
      )
        continue;

      // Ensure one is on each side
      const leftSpouse = spouse1.virtualX < person.virtualX ? spouse1 : spouse2;
      const rightSpouse =
        spouse1.virtualX > person.virtualX ? spouse1 : spouse2;

      if (leftSpouse === rightSpouse) continue;
      if (
        leftSpouse.virtualX >= person.virtualX ||
        rightSpouse.virtualX <= person.virtualX
      )
        continue;

      const distLeft = person.virtualX - leftSpouse.virtualX;
      const distRight = rightSpouse.virtualX - person.virtualX;

      if (Math.abs(distLeft - distRight) < 0.01) continue;

      const targetDist = Math.min(distLeft, distRight);

      if (distLeft > distRight) {
        const newX = person.virtualX - targetDist;
        const leftNeighbor = this.persons
          .filter(
            (p) =>
              p.rowDepth === person.rowDepth && p.virtualX < leftSpouse.virtualX
          )
          .sort((a, b) => b.virtualX - a.virtualX)[0];

        if (
          leftNeighbor &&
          newX - leftNeighbor.virtualX <
            config.VIRTUAL_X_REQUIRED_BETWEEN_PEOPLE
        ) {
          continue;
        }
        leftSpouse.virtualX = newX;
      } else {
        const newX = person.virtualX + targetDist;
        const rightNeighbor = this.persons
          .filter(
            (p) =>
              p.rowDepth === person.rowDepth &&
              p.virtualX > rightSpouse.virtualX
          )
          .sort((a, b) => a.virtualX - b.virtualX)[0];

        if (
          rightNeighbor &&
          rightNeighbor.virtualX - newX <
            config.VIRTUAL_X_REQUIRED_BETWEEN_PEOPLE
        ) {
          continue;
        }
        rightSpouse.virtualX = newX;
      }

      processed.add(person.pid);
      processed.add(spouse1.pid);
      processed.add(spouse2.pid);
    }
  }

  executeLayout() {
    this.calculateGenerationsFromRelationships();
    this.alignDisconnectedComponentsToTop();

    this.createSiblingGroups();

    const rowDepthGroups = buildRowDepthGroups(this);
    this.assignDefaultVirtualX(rowDepthGroups);
    this.calculateRightDistancesBetweenPeople(rowDepthGroups);

    this.fixPositionOfParents(rowDepthGroups);

    this.squeezeGenerationsHorizontally(rowDepthGroups);

    this.removeAllEmptyRangesOfX();

    if (config.ENABLE_BRING_PARENTS_CLOSER) {
      this.bringMotherFatherCloserToEachOther();
      this.fixPositionOfParents(rowDepthGroups);
      this.removeAllEmptyRangesOfX();
    }

    this.equalizeFirstTwoSpouseDistances();

    this.addSpacingBetweenDisconnectedComponents();
    normalizeXToStartAtZero(this.persons);

    this.finalizeUnknownPersonPositions();

    const positions = this.convertToCoordinates();

    return positions;
  }
}
